// WP-17 (US 6.2, LLD-05 O-02 bell) — the office notification bell, now backed by
// the PERSISTENT notification store (GET /notifications) instead of WP-26's
// feed-derived bell + localStorage "last seen" (D-WP26-1 closed). Unread state is
// server-side (readAt) and cross-device; "mark all as read" is a mutation. Each
// item deep-links to its entity (job/quote/org). Rendered only for OWNER /
// OFFICE_MANAGER (the API enforces the RBAC; bookkeeper scope stays tight, D-S4).
//
// UX-4a (UX v1.7 §3 / §7 / §8; markup + presentation only — the feed, the
// mutation and the deep links are unchanged):
//   - UX2-SHELL-7: the panel is a labelled region, not role="menu" (a list of
//     links is not a menu — axe aria-required-children); the trigger drops
//     aria-haspopup and points aria-controls at the open panel.
//   - UX2-NOTIF-5: the trigger's accessible name carries the unread count (the
//     badge is visual only), and an always-mounted polite status line
//     announces "All notifications marked as read." after the mutation.
//   - Row 96 guard: a trailing " (https://…)" is dropped from the shown message
//     (notificationDisplayMessage); the producer fix is UX-6.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../auth/session-store";
import { t } from "../i18n/i18n";
import { dateTime } from "../i18n/tenant-time";
import {
  notificationDisplayMessage,
  notificationLink,
} from "../features/notifications/notifications-contracts";
import {
  useMarkAllNotificationsRead,
  useNotifications,
} from "../features/notifications/notifications-vm";

// WP-30 (V5): the tenant's zone. `toLocaleString` with no `timeZone` renders
// in the BROWSER's zone — right only while every operator sits in the tenant's
// country, and silently wrong otherwise.
const formatDateTime = dateTime;

const PANEL_ID = "bell-panel";

export function NotificationBell() {
  const role = useSession((s) => s.role);
  const canSee = role === "OWNER" || role === "OFFICE_MANAGER";
  const [open, setOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const query = useNotifications(canSee);
  const markAllRead = useMarkAllNotificationsRead();
  const notifications = useMemo(() => query.data?.notifications ?? [], [query.data]);
  const unread = query.data?.unreadCount ?? 0;

  if (!canSee) return null;

  return (
    <span className="bell">
      <button
        type="button"
        className="button button--ghost bell__button"
        aria-label={
          unread > 0 ? t("notif.labelUnread", { count: String(unread) }) : t("bell.label")
        }
        aria-expanded={open}
        aria-controls={open ? PANEL_ID : undefined}
        data-testid="notification-bell"
        onClick={() => {
          setAnnouncement("");
          setOpen((v) => !v);
        }}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 ? (
          <span className="bell__badge" data-testid="bell-unread" aria-hidden="true">
            {unread}
          </span>
        ) : null}
      </button>
      <span className="visually-hidden" role="status" data-testid="bell-status">
        {announcement}
      </span>
      {open ? (
        <section
          id={PANEL_ID}
          className="bell__panel"
          aria-label={t("notif.title")}
          data-testid="bell-panel"
        >
          <div className="bell__panel-head">
            <strong>{t("notif.title")}</strong>
            {notifications.length > 0 ? (
              <button
                type="button"
                className="button button--ghost"
                onClick={() =>
                  markAllRead.mutate(undefined, {
                    onSuccess: () => setAnnouncement(t("notif.allMarkedRead")),
                  })
                }
                data-testid="bell-mark-read"
              >
                {t("notif.markAllRead")}
              </button>
            ) : null}
          </div>
          {notifications.length === 0 ? (
            <p className="bell__empty" data-testid="bell-empty">
              {t("notif.empty")}
            </p>
          ) : (
            <ul className="bell__list">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={n.readAt ? "bell__item" : "bell__item bell__item--unread"}
                >
                  <Link
                    to={notificationLink(n)}
                    onClick={() => setOpen(false)}
                    data-testid={`bell-item-${n.id}`}
                  >
                    <span className="bell__item-title">{notificationDisplayMessage(n.message)}</span>
                    <span className="bell__item-meta">{formatDateTime(n.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </span>
  );
}
